import React from 'react'
import { withTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { Folder01Icon, Search01Icon } from '@hugeicons/core-free-icons'
import Icon from './Icon'
import { getColorForIconUrl } from 'components/pointOfInterest/poiSearchResults/poiIconUtils'
import { formatCompactNumber } from '../../utils'

const PoiTooltip = ({
  t,
  isRightPaneVisible,
  name,
  address,
  type,
  iconUrl,
  isGeneral,
  isAlly,
  isCompetitor,
  variant = 'marker', // 'marker' | 'polygon' | 'target-group'
  onViewMore,
  style,
  population,
  shapesCount,
  generalCount,
  allyCount,
  competitorCount,
  area,
  onMouseEnterTooltip,
  onMouseLeaveTooltip,
  iconId,
  iconColor
}) => {
  const bucketLabel = (() => {
    if (isCompetitor) return t('POI_TOOLTIP.COMPETITOR') || 'Competitor'
    if (isAlly) return t('POI_TOOLTIP.ALLY') || 'Ally'
    if (isGeneral) return t('POI_TOOLTIP.GENERAL') || 'General'
    return null
  })()

  const resolvedIconSrc = iconUrl ? `${iconUrl}.png` : 'https://maps.gstatic.com/mapfiles/place_api/icons/v2/generic_pinlet.png'
  const typeColor = iconUrl ? getColorForIconUrl(iconUrl) : null

  const formatType = (typeString) => {
    if (!typeString) return ''
    return typeString
      .replace(/_/g, ' ')
      .replace(/^\w/, c => c.toUpperCase())
  }

  const formattedType = variant === 'marker' && type ? formatType(type) : type

  const baseClass = 'poi-tooltip__container'
  const variantClass =
    variant === 'target-group'
      ? 'poi-tooltip__container--target-group'
      : variant === 'polygon'
        ? 'poi-tooltip__container--polygon'
        : 'poi-tooltip__container--marker'

  const shouldRenderContent =
    variant === 'marker' || variant === 'polygon' || isRightPaneVisible

  return (
    <div
      className={`${baseClass} ${variantClass}`}
      style={style}
      onClick={(e) => {
        e.stopPropagation()
      }}
      onMouseDown={(e) => {
        e.stopPropagation()
      }}
      onMouseEnter={(e) => {
        e.stopPropagation()
        if (typeof onMouseEnterTooltip === 'function') {
          onMouseEnterTooltip()
        }
      }}
      onMouseLeave={(e) => {
        e.stopPropagation()
        if (typeof onMouseLeaveTooltip === 'function') {
          onMouseLeaveTooltip()
        }
      }}
    >
      {shouldRenderContent && (
        <div className='poi-tooltip__pane'>
          <div className='poi-tooltip__content'>
            <div className='poi-tooltip__header'>
              <span aria-hidden='true' className='poi-tooltip__shape'>
                {variant === 'marker' && (
                  <span>
                    <Icon id='fa-drop' />
                    <Icon id='fa-drop' />
                  </span>
                )}
                {variant === 'polygon' && (
                  <span
                    style={{
                      color: iconColor || '#33354D',
                      backgroundColor: '#F6F6F6',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                    className='poi-tooltip__shape-icon'
                  >
                    <Icon id={iconId || 'polygon'} style={{ width: '18px', height: '18px' }} />
                  </span>
                )}
                {variant === 'target-group' && (
                  <span
                    style={{
                      color: iconColor || '#334155',
                      backgroundColor: '#292C440A',
                      borderRadius: '8px',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    className='poi-tooltip__shape-icon'
                  >
                    <HugeiconsIcon icon={Folder01Icon} size={18} color={iconColor || '#334155'} />
                  </span>
                )}
              </span>
              <div className='poi-tooltip__title-wrap'>
                <h3 className='poi-tooltip__title'>{name}</h3>
                {address && <p className='poi-tooltip__address'>{address}</p>}
              </div>
            </div>
            {variant === 'target-group' && (
              <div>
                {population != null && (
                  <span className='poi-tooltip__info-item'>
                    <Icon id='fa-population' />
                    <span aria-label={t('TARGET_GROUP.TOOLTIP_POPULATION') || 'Population'}>{formatCompactNumber(population)}</span>
                  </span>
                )}
                {shapesCount != null && (
                  <span className='poi-tooltip__info-item'>
                    <Icon id='fa-links' />
                    <span aria-label={t('TARGET_GROUP.TOOLTIP_SHAPES') || 'Shapes'}>{shapesCount}</span>
                  </span>
                )}
                <div className='poi-tooltip__bucket-info'>
                  <span className='poi-tooltip__group-label'>POIs:</span>
                  {generalCount != null && (
                    <span className='poi-tooltip__info-item poi-tooltip__info-item--general'>
                      <Icon id='fa-drop' />
                      <span>{t('TARGET_GROUP.TOOLTIP_GENERAL') || 'General'}: {generalCount}</span>
                    </span>
                  )}
                  {allyCount != null && (
                    <span className='poi-tooltip__info-item poi-tooltip__info-item--ally'>
                      <Icon id='fa-drop' />
                      <span>{t('TARGET_GROUP.TOOLTIP_ALLY') || 'Allies'}: {allyCount}</span>
                    </span>
                  )}
                  {competitorCount != null && (
                    <span className='poi-tooltip__info-item poi-tooltip__info-item--competitor'>
                      <Icon id='fa-drop' />
                      <span>{t('TARGET_GROUP.TOOLTIP_COMPETITOR') || 'Competitors'}: {competitorCount}</span>
                    </span>
                  )}
                </div>
              </div>
            )}
            {variant === 'marker' && (
              <div>
                {formattedType && (
                  <div className='poi-tooltip__info-item'>
                    <span
                      className='poi-tooltip__type-icon'
                      style={{
                        backgroundColor: typeColor || '#b068f9',
                        WebkitMaskImage: `url(${resolvedIconSrc})`,
                        maskImage: `url(${resolvedIconSrc})`
                      }}
                    />
                    <span className='poi-tooltip__type-text'>{formattedType}</span>
                  </div>
                )}
                {bucketLabel && (
                  <div className={`poi-tooltip__info-item poi-tooltip__info-item--${bucketLabel.toLowerCase()}`}>
                    <Icon id='fa-drop' />
                    <span>{bucketLabel}</span>
                  </div>
                )}
              </div>
            )}
            {variant === 'polygon' && (
              <div>
                {population != null && (
                  <span className='poi-tooltip__info-item'>
                    <Icon id='fa-population' />
                    <span>{t('TARGET_GROUP.TOOLTIP_POPULATION') || 'Population'}: {formatCompactNumber(population)}</span>
                  </span>
                )}
                {area != null && (
                  <span className='poi-tooltip__info-item'>
                    <Icon id='fa-circles' />
                    <span>{area.toFixed(1)}/km²</span>
                  </span>
                )}
              </div>
            )}
            {(variant !== 'marker' || onViewMore) && (
              <button
                type='button'
                className='poi-tooltip__button'
                onMouseDown={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  if (onViewMore) {
                    onViewMore()
                  }
                }}
              >
                <HugeiconsIcon icon={Search01Icon} size={14} color='#FFFFFF' className='poi-tooltip__button-icon' />
                <span className='poi-tooltip__button-label'>{t('POI_TOOLTIP.VIEW_MORE')}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default withTranslation()(PoiTooltip)
